import React, { useState, useEffect } from 'react';
import { Button } from '../../../../components/ui/button';
import { Badge } from '../../../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { 
  Activity,
  Zap,
  Info,
  RefreshCw,
  Target,
  Globe,
  Sparkles
} from 'lucide-react';
import { 
  loggingApiService, 
  SecurityActivitySummary, 
  SecurityRecommendations, 
  ThreatContext,
  SecurityPostureScore,
  LexExplainRequest,
  LexSecurityExplainData,
  LexSecurityReference,
  LexSecurityTimeframe
} from '../../../../services/loggingApiService';
import { LexExplainModal } from './LexExplainModal';

/**
 * Simplified Security Alerts Panel Component
 * 
 * Provides clean security alert monitoring with our current interface structure
 */
export const SecurityAlertsPanel: React.FC = () => {
  const [activitySummary, setActivitySummary] = useState<SecurityActivitySummary | null>(null);
  const [recommendations, setRecommendations] = useState<SecurityRecommendations | null>(null);
  const [threatContext, setThreatContext] = useState<ThreatContext | null>(null);
  const [postureScore, setPostureScore] = useState<SecurityPostureScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [timeframe, setTimeframe] = useState<'24h' | '7d' | '30d'>('7d');
  const [lexOpen, setLexOpen] = useState(false);
  const [lexLoading, setLexLoading] = useState(false);
  const [lexReferenceLoading, setLexReferenceLoading] = useState(false);
  const [lexError, setLexError] = useState<string | null>(null);
  const [lexTitle, setLexTitle] = useState<string>('Lex Explanation');
  const [lexSubtitle, setLexSubtitle] = useState<string | undefined>(undefined);
  const [lexExplanation, setLexExplanation] = useState<string | undefined>(undefined);
  const [lexProvider, setLexProvider] = useState<string | undefined>(undefined);
  const [lexModel, setLexModel] = useState<string | undefined>(undefined);
  const [lexReferences, setLexReferences] = useState<LexSecurityReference[] | undefined>(undefined);
  const [lexStack, setLexStack] = useState<Array<{
    title: string;
    subtitle?: string;
    explanation?: string;
    provider?: string;
    model?: string;
    references?: LexSecurityReference[];
  }>>([]);
  const [lastLexRequest, setLastLexRequest] = useState<LexExplainRequest | null>(null);
  const [lastSecurityOverviewTimeframe, setLastSecurityOverviewTimeframe] = useState<LexSecurityTimeframe | null>(null);
  const [lastSecurityReference, setLastSecurityReference] = useState<LexSecurityReference | null>(null);

  useEffect(() => {
    // Timeframe change is user-driven; show a real loading state for clarity.
    setActivitySummary(null);
    setRecommendations(null);
    setThreatContext(null);
    setPostureScore(null);
    loadSecurityData({ mode: 'initial' });
  }, [timeframe]);

  // Add auto-refresh for real-time data
  useEffect(() => {
    const interval = setInterval(() => {
      // Avoid jarring refreshes while the user is reading a Lex explanation.
      if (lexOpen) return;
      // Avoid unnecessary work in background tabs.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      // Avoid overlapping refreshes.
      if (loading || refreshing) return;
      loadSecurityData({ mode: 'refresh' });
    }, 90000); // 90-second refresh

    return () => clearInterval(interval);
  }, [lexOpen, loading, refreshing, timeframe]);

  const loadSecurityData = async (opts?: { mode?: 'initial' | 'refresh' }) => {
    const mode = opts?.mode || 'refresh';
    try {
      if (mode === 'initial') setLoading(true);
      else setRefreshing(true);
      const [summary, recs, threats, posture] = await Promise.all([
        loggingApiService.getSecurityActivitySummary(timeframe),
        loggingApiService.getSecurityRecommendations(timeframe),
        loggingApiService.getThreatContext(),
        loggingApiService.getSecurityPostureScore(timeframe)
      ]);
      
      setActivitySummary(summary);
      setRecommendations(recs);
      setThreatContext(threats);
      setPostureScore(posture);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to load security data:', error);
    } finally {
      if (mode === 'initial') setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadSecurityData({ mode: (activitySummary || recommendations || threatContext || postureScore) ? 'refresh' : 'initial' });
  };

  const runLex = async (request: LexExplainRequest, opts?: { title?: string; subtitle?: string }) => {
    setLexTitle(opts?.title || 'Lex Explanation');
    setLexSubtitle(opts?.subtitle);
    setLexError(null);
    setLexExplanation(undefined);
    setLexProvider(undefined);
    setLexModel(undefined);
    setLexReferences(undefined);
    setLexStack([]);
    setLastSecurityOverviewTimeframe(null);
    setLastSecurityReference(null);
    setLexLoading(true);
    setLexOpen(true);
    setLastLexRequest(request);

    try {
      const res = await loggingApiService.explainWithLex(request);
      setLexExplanation(res.explanation);
      setLexProvider(res.provider);
      setLexModel(res.model);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to generate Lex explanation');
    } finally {
      setLexLoading(false);
    }
  };

  const applyLexSecurityResponse = (res: LexSecurityExplainData) => {
    setLexExplanation(res.explanation);
    setLexProvider(res.provider);
    setLexModel(res.model);
    setLexReferences(Array.isArray(res.references) ? res.references : []);
  };

  const runLexSecurityOverview = async (tf: LexSecurityTimeframe) => {
    setLexTitle('Lex: Security Alerts');
    setLexSubtitle(`Timeframe: ${tf}`);
    setLexError(null);
    setLexExplanation(undefined);
    setLexProvider(undefined);
    setLexModel(undefined);
    setLexReferences(undefined);
    setLexStack([]);
    setLexReferenceLoading(false);
    setLexLoading(true);
    setLexOpen(true);
    setLastSecurityOverviewTimeframe(tf);
    setLastSecurityReference(null);
    setLastLexRequest(null);

    try {
      const res = await loggingApiService.explainSecurityOverview({
        timeframe: tf,
        includeEvidence: true,
      });
      applyLexSecurityResponse(res);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to generate Lex security explanation');
    } finally {
      setLexLoading(false);
    }
  };

  const runLexSecurityReference = async (ref: LexSecurityReference, opts?: { pushStack?: boolean }) => {
    const tf = (ref?.filters?.timeframe as LexSecurityTimeframe) || lastSecurityOverviewTimeframe || (timeframe as LexSecurityTimeframe);
    const pushStack = opts?.pushStack !== false;

    if (pushStack) {
      setLexStack((prev) => [
        ...prev,
        { title: lexTitle, subtitle: lexSubtitle, explanation: lexExplanation, provider: lexProvider, model: lexModel, references: lexReferences },
      ]);
    }

    setLexTitle(`Lex: ${ref.label}`);
    setLexSubtitle(`Timeframe: ${tf}`);
    setLexError(null);
    setLexExplanation(undefined);
    setLexProvider(undefined);
    setLexModel(undefined);
    setLexReferenceLoading(true);
    setLastSecurityReference(ref);

    try {
      const res = await loggingApiService.explainSecurityReference({
        timeframe: tf,
        reference: ref,
        maxItems: 25,
      });
      applyLexSecurityResponse(res);
    } catch (e) {
      setLexError(e instanceof Error ? e.message : 'Failed to load evidence explanation');
    } finally {
      setLexReferenceLoading(false);
    }
  };

  const handleLexBack = () => {
    setLexStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      const last = prev[prev.length - 1];

      setLexTitle(last.title);
      setLexSubtitle(last.subtitle);
      setLexExplanation(last.explanation);
      setLexProvider(last.provider);
      setLexModel(last.model);
      setLexReferences(last.references);
      setLexError(null);
      setLastSecurityReference(null);

      return next;
    });
  };

  const handleLexRetry = () => {
    if (lexLoading || lexReferenceLoading) return;
    if (lastSecurityReference) {
      runLexSecurityReference(lastSecurityReference, { pushStack: false });
      return;
    }
    if (lastSecurityOverviewTimeframe) {
      runLexSecurityOverview(lastSecurityOverviewTimeframe);
      return;
    }
    if (lastLexRequest) {
      runLex(lastLexRequest, { title: lexTitle, subtitle: lexSubtitle });
    }
  };

  if (loading && !activitySummary && !recommendations && !threatContext && !postureScore) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-2">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span>Loading security data...</span>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Security Alerts</h2>
          <p className="text-sm text-gray-500">Monitor security events and recommendations</p>
        </div>
        <div className="flex items-center space-x-2">
          <select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value as '24h' | '7d' | '30d')}
            className="px-3 py-1 border rounded-md text-sm"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
          <Button
            onClick={() => {
              runLexSecurityOverview(timeframe as LexSecurityTimeframe);
            }}
            variant="outline"
            size="sm"
            disabled={loading || refreshing}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Lex
          </Button>
          <Button 
            onClick={handleRefresh} 
            variant="outline" 
            size="sm"
            disabled={loading || refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${(loading || refreshing) ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Security Activity Summary */}
      {activitySummary && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Activity className="w-5 h-5 mr-2" />
              Security Activity Summary
            </CardTitle>
            <CardDescription>
              Security events and risk analysis for the selected timeframe
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-lex-accent">
                  {activitySummary.summary.totalSecurityEvents}
                </div>
                <div className="text-sm text-gray-500">Total Events</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">
                  {activitySummary.summary.criticalEvents}
                </div>
                <div className="text-sm text-gray-500">Critical Events</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {activitySummary.summary.highRiskActivities}
                </div>
                <div className="text-sm text-gray-500">High Risk</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {activitySummary.summary.activeThreats}
                </div>
                <div className="text-sm text-gray-500">Active Threats</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Recommendations */}
      {recommendations && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Target className="w-5 h-5 mr-2" />
              Security Recommendations
            </CardTitle>
            <CardDescription>
              Suggested improvements for your security posture
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recommendations.recommendations && recommendations.recommendations.length > 0 ? (
                recommendations.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <Info className="w-5 h-5 text-lex-accent mt-0.5" />
                    <div>
                      <div className="font-medium">{rec.title}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{rec.description}</div>
                      <Badge variant="secondary" className="mt-2">
                        {rec.priority} Priority
                      </Badge>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 text-gray-500">
                  No recommendations at this time
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}


      {/* Last Refresh */}
      <div className="text-center text-sm text-gray-500">
        Last updated: {lastRefresh.toLocaleTimeString()}{refreshing ? ' (updating…)': ''}
      </div>
    </div>

    <LexExplainModal
      isOpen={lexOpen}
      title={lexTitle}
      subtitle={lexSubtitle}
      explanation={lexExplanation}
      provider={lexProvider}
      model={lexModel}
      references={lexReferences as any}
      referenceLoading={lexReferenceLoading}
      loading={lexLoading}
      error={lexError}
      onClose={() => setLexOpen(false)}
      canBack={lexStack.length > 0}
      onBack={lexStack.length > 0 ? handleLexBack : undefined}
      onReferenceClick={(ref) => runLexSecurityReference(ref as LexSecurityReference)}
      onRetry={(lastLexRequest || lastSecurityOverviewTimeframe || lastSecurityReference) ? handleLexRetry : undefined}
    />
    </>
  );
};
